import type { Baseline } from '@shared/models/project.model';
import type { ProposalId } from '@shared/models/proposals.model';
import {
  ProjectChangeType,
  type ProjectChange,
} from '@shared/helpers/pipeline/update-engine.models';
import {
  flattenElements,
  getElementById,
  isElement,
} from '@shared/helpers/recursive-element.helpers';
import { getAffectedElementIds } from './action.helpers';
import { buildDiscardedRowIdsChangesForElementRemoval } from '@shared/helpers/candidate-discard.helpers';

/**
 * Whether deleting {@link elementId} while {@link proposalId} is selected may
 * remove it from baseline for real. True only when the element was created in
 * that proposal ({@link IElement.createdInProposalId}), is still deactivated in
 * baseline, and no *other* proposal has any action touching it or its
 * descendants — so nobody else can lose anything. A user-deactivated baseline
 * element (no `createdInProposalId`) is never removed this way.
 */
export const isElementOwnedOnlyByProposal = (
  version: Baseline,
  proposalId: ProposalId,
  elementId: string,
): boolean => {
  const element = getElementById(version, elementId, false);
  if (!element || !isElement(element)) return false;
  if (element.createdInProposalId !== proposalId) return false;
  if (!element.isDeactivated) return false;

  const subtreeIds = new Set(flattenElements(element).map((child) => child.id));
  subtreeIds.add(elementId);

  const actionsById = new Map(
    (version.actions ?? []).map((action) => [action.id, action]),
  );
  for (const proposal of version.proposals ?? []) {
    if (proposal.id === proposalId) continue;
    for (const actionId of proposal.actionIds) {
      const action = actionsById.get(actionId);
      if (!action) continue;
      if (
        getAffectedElementIds(action.changes).some((id) => subtreeIds.has(id))
      ) {
        return false;
      }
    }
  }
  return true;
};

/**
 * Changes for deleting {@link elementIds} while {@link proposalId} is selected.
 * Elements the proposal owns outright (see {@link isElementOwnedOnlyByProposal})
 * are removed from baseline — the hard remove also drops the proposal's own
 * actions on them and, for imported rows, skips the row like a baseline delete.
 * Everything else is a proposal-scoped soft delete that leaves baseline and
 * the import bookkeeping untouched (DEV-3702).
 */
export const buildRemoveElementsInProposalChanges = (
  version: Baseline,
  proposalId: ProposalId,
  elementIds: readonly string[],
): ProjectChange[] => {
  const hardIds = elementIds.filter((id) =>
    isElementOwnedOnlyByProposal(version, proposalId, id),
  );
  const changes: ProjectChange[] = elementIds.map((elementId) =>
    hardIds.includes(elementId)
      ? { type: ProjectChangeType.ElementRemove, elementId }
      : { type: ProjectChangeType.ElementSoftDelete, elementId },
  );
  return [
    ...changes,
    ...buildDiscardedRowIdsChangesForElementRemoval(version, hardIds),
  ];
};
